/**
 * ProtectedRoute.jsx
 *
 * Route guard that redirects unauthenticated users to /auth.
 *
 * While the initial session check is in progress (`loading === true`),
 * a full-screen spinner is shown to prevent a flash of the protected content.
 * Once loading completes, users without a session are redirected to /auth
 * with the original location stored in `state.from` so they can be sent
 * back after signing in.
 *
 * Usage (in Routes.jsx):
 *   <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Wraps a route element and enforces authentication.
 *
 * @param {{ children: React.ReactNode }} props
 */
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Show a spinner while the session is being hydrated from storage
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth, preserving the intended destination
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
